/**
 * IntercompanyTabelle (A-9 / D-072 „Variante C") — IC-Kosten je Konto und Quartal.
 *
 * VORHER zeigte die Tabelle eine Spalte „Q1" aus `intercompany[].q1`. Seit D-072
 * weist der Snapshot mit `q_ist` ein echtes Quartals-IST aus (Differenz
 * aufeinanderfolgender SuSa-Stichtage) statt viermal desselben fortgeschriebenen
 * Q1-Werts. `q1` bleibt als abgeleiteter ALIAS auf `q_ist.q1` erhalten.
 *
 * DREI harte Regeln, die diese Komponente durchsetzt:
 *
 * 1. **Q3/Q4 sind LEER, nicht 0.** `q_ist.q3/q4` ist `null`, wo kein SuSa-Stichtag
 *    im Quartal liegt. Eine dargestellte Null wäre von einer echten Null nicht
 *    unterscheidbar — also eine Falschaussage.
 * 2. **Das Sternchen und der Unvollständigkeits-Hinweis kommen aus dem Snapshot**,
 *    nämlich aus `quarterly[qX].status` — derselben Quelle, aus der auch die
 *    Soll/Ist-Matrix und der FCFE-Block ihre Status-Badges ziehen. KEIN
 *    hartkodierter Monat und kein zweiter Mechanismus: ein fester Monat im Code
 *    veraltet mit dem nächsten Snapshot still (dasselbe Muster wie beim
 *    R-003-Hinweis, Commit 235d904).
 * 3. **`fehlt_2026` wird sichtbar gemacht.** Ein Konto, das in KEINER SuSa des
 *    laufenden Jahres eine Zeile führt, läuft mit 0 mit. Diese 0 ist keine
 *    fachliche Null und darf nicht als eine gelesen werden.
 *
 * FALLBACK: Führt keine Zeile `q_ist` (älterer Backend-Cache-Stand), entfallen die
 * Quartalsspalten und es bleibt die bisherige Darstellung Q1 | VJ. Die Alternative
 * — Q2–Q4 als „—" zeigen — behauptete, es gäbe dort keine Werte; richtig ist, dass
 * sie unbekannt sind.
 */
import type { Entity, IntercompanyRow, QuarterKey, QuarterStatus } from '../types/snapshot';
import { QuarterStatusBadge } from './QuarterStatusBadge';
import { DASH, formatEur } from '../lib/format';
import { JAHRE_FALLBACK, useJahre } from '../lib/jahre';
import type { Jahre } from '../lib/jahre';

const QUARTER_COLS: { key: QuarterKey; qIst: keyof NonNullable<IntercompanyRow['q_ist']>; label: string }[] = [
  { key: 'q1_2026', qIst: 'q1', label: 'Q1' },
  { key: 'q2_2026', qIst: 'q2', label: 'Q2' },
  { key: 'q3_2026', qIst: 'q3', label: 'Q3' },
  { key: 'q4_2026', qIst: 'q4', label: 'Q4' },
];

/** Ein Quartal ist unvollständig, solange kein Quartalsende-SuSa vorliegt (D-031 §2.3). */
const UNVOLLSTAENDIG: ReadonlySet<QuarterStatus> = new Set<QuarterStatus>(['active']);

/**
 * Texte aus EINER Quelle (Tests prüfen gegen genau diese Strings). D-089: Jahreszahlen
 * kommen aus Berichtsjahr (bj) / Vorjahr (vj) — nie mehr hart kodiert.
 */
export function icTexte({ bj, vj }: Jahre) {
  return {
    titel: 'Intercompany-Kosten',
    /** Erklärt genau die Markierung, die die Kopfzeile setzt — eine Quelle, ein Text. */
    unvollstaendigHinweis:
      '* Das Quartal ist noch nicht abgeschlossen (kein Quartalsende-Stichtag): ' +
      'der Wert deckt nur die bereits vorliegenden Monate ab und ist keine ' +
      'Quartalssumme.',
    leerHinweis:
      '„—" = kein Stichtag in diesem Quartal. Es wird bewusst keine 0 ausgewiesen: ' +
      'eine 0 wäre von einer echten Null nicht zu unterscheiden.',
    fehltBadge: `Konto ${bj} nicht gebucht`,
    fehltTitle:
      'Dieses Konto führt in keiner SuSa des laufenden Jahres eine Zeile und läuft ' +
      'mit 0 mit. Die 0 ist keine fachliche Null, sondern eine fehlende Buchung.',
    /** D-080: Prognose-Zellen (Q-Soll) und Jahresspalte. */
    jahrKopf: 'Jahr Prognose',
    prognoseTitle:
      `Prognose: Q-Soll = JA ${vj} / 4 (kein SuSa-Ist in diesem Quartal). Dieselbe ` +
      'Auswahl, mit der „Jahr Prognose" rechnet (D-080).',
    prognoseHinweis:
      `Kursive Werte sind Prognose: das Q-Soll (JA ${vj} / 4, D-057-Definition), wo ` +
      'kein SuSa-Ist vorliegt. „Jahr Prognose" = je Quartal Ist, wo vorhanden, sonst Q-Soll.',
    /** D-085 (Prompt 128 §3): Prognose aus der Ist-Monatsrate. Wortlaut laut Vorgabe. */
    prognoseRateTitle: 'Prognose: Ist-Monatsrate des laufenden Jahres × Restmonate (D-085)',
    prognoseRateHinweis:
      'Kursive Werte sind Prognose aus der Ist-Monatsrate des laufenden Jahres (D-085): ' +
      'im laufenden Quartal Ist der vorliegenden Monate zzgl. Rate × Restmonate, in ' +
      'künftigen Quartalen Rate × 3. „Jahr Prognose" = Ist-Monatsrate × 12.',
    istBisher: 'Ist bisher',
    unbebuchtBadge: `${bj} unbebucht`,
    unbebuchtTitle: `${bj} unbebucht, im VJ bebucht — siehe Hinweise`,
    /** Prompt 130 §1: abgeschlossenes Quartal ohne Ist → „—", weder Prognose noch Soll. */
    keinStichtagTitle: 'Kein SuSa-Stichtag in diesem Quartal',
  } as const;
}

/** Texte für die Fallback-Jahre (Snapshot vor D-089) — für Tests und Rückwärtskompatibilität. */
export const IC_TEXTE = icTexte(JAHRE_FALLBACK);

/** Gemeinsamer Stil der Zeilen-Hinweis-Badges (fehlt_2026, unbebucht_2026) — eine Quelle. */
const HINWEIS_BADGE_KLASSE =
  'cursor-help rounded border border-ampel-yellow/40 bg-ampel-yellow/10 px-1 text-[10px] font-medium text-ampel-yellow';

export interface IntercompanyTabelleProps {
  entity: Entity;
}

export function IntercompanyTabelle({ entity }: IntercompanyTabelleProps) {
  // Hook vor dem frühen return (Rules of Hooks). D-089: Texte im Berichtsjahr.
  const T = icTexte(useJahre());
  const rows = entity.intercompany;
  if (!rows?.length) return null;

  // Quartalsanzeige nur, wenn der Snapshot sie trägt (D-072). Ein älterer
  // Cache-Stand ohne q_ist führt zur bisherigen Darstellung.
  const hatQIst = rows.some((r) => r.q_ist !== undefined);
  // D-080: Prognose-Anzeige nur, wenn der Snapshot die neuen Felder trägt.
  const hatJahr = rows.some((r) => r.jahr !== undefined);

  // Status je Quartal AUS DEM SNAPSHOT. `undefined`, wo das Quartalsobjekt fehlt.
  const status: Partial<Record<QuarterKey, QuarterStatus>> = {};
  for (const { key } of QUARTER_COLS) {
    status[key] = entity.quarterly[key]?.status;
  }

  // Sternchen nur, wo der Snapshot ein unvollständiges Quartal MIT Wert ausweist.
  // Ohne Wert braucht es keine Einschränkung — dort steht ohnehin „—".
  const markiert = QUARTER_COLS.filter(({ key, qIst }) => {
    const s = status[key];
    if (!s || !UNVOLLSTAENDIG.has(s)) return false;
    return rows.some((r) => r.q_ist?.[qIst] != null);
  }).map(({ key }) => key);

  const zeigtFehlend = rows.some((r) => r.fehlt_2026 === true);
  // D-085: neue Prognose-Regel JE ZEILE, sobald sie q_prognose führt (null = keine).
  // Bei diesen Zeilen wandert das Sternchen an die „Ist bisher"-Zeile — ihr Hauptwert
  // ist eine Quartals-PROGNOSE. Zeilen OHNE q_prognose behalten Kopf-Sternchen und
  // D-080-Erklärung; beides gilt je Zeilenart, nicht tabellenweit (Review-Befund 2).
  const mitRate = (r: IntercompanyRow) => r.q_prognose != null;
  const hatRate = rows.some(mitRate);
  const hatAlt = rows.some((r) => !mitRate(r));
  const kopfMarkiert = QUARTER_COLS.filter(({ key, qIst }) => {
    const s = status[key];
    if (!s || !UNVOLLSTAENDIG.has(s)) return false;
    return rows.some((r) => !mitRate(r) && r.q_ist?.[qIst] != null);
  }).map(({ key }) => key);

  return (
    <section aria-label={T.titel}>
      <h3 className="mb-3 text-lg font-semibold text-text">{T.titel}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-surface p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th scope="col" className="py-2 font-medium">Konto</th>
              <th scope="col" className="py-2 font-medium">Bezeichnung</th>
              {hatQIst ? (
                QUARTER_COLS.map(({ key, label }) => (
                  <th key={key} scope="col" className="py-2 text-right font-medium">
                    <div className="flex flex-col items-end gap-1">
                      <span>
                        {label}
                        {kopfMarkiert.includes(key) && (
                          <span title={T.unvollstaendigHinweis}>*</span>
                        )}
                      </span>
                      {status[key] ? (
                        <QuarterStatusBadge status={status[key]!} />
                      ) : (
                        <span className="text-xs font-normal italic text-text-muted">{DASH}</span>
                      )}
                    </div>
                  </th>
                ))
              ) : (
                <th scope="col" className="py-2 text-right font-medium">Q1</th>
              )}
              {hatQIst && hatJahr && (
                <th scope="col" className="py-2 text-right font-medium">{T.jahrKopf}</th>
              )}
              <th scope="col" className="py-2 text-right font-medium">VJ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((ic) => (
              <tr key={ic.konto} className="border-b border-border/60">
                <td className="py-1.5 tabular-nums">{ic.konto}</td>
                <th scope="row" className="py-1.5 text-left font-normal">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {ic.label}
                    {ic.fehlt_2026 === true && (
                      <span
                        title={T.fehltTitle}
                        className={HINWEIS_BADGE_KLASSE}
                      >
                        {T.fehltBadge}
                      </span>
                    )}
                    {ic.unbebucht_2026 === true && (
                      <span
                        title={T.unbebuchtTitle}
                        className={HINWEIS_BADGE_KLASSE}
                      >
                        {T.unbebuchtBadge}
                      </span>
                    )}
                  </span>
                </th>
                {hatQIst ? (
                  QUARTER_COLS.map(({ key, qIst }) => {
                    // `q_ist` ist optional -> der Zugriff MUSS den Fehlfall behandeln.
                    // formatEur(undefined|null) liefert "—", nie 0. D-080: wo kein Ist
                    // liegt und der Snapshot ein Q-Soll trägt, steht die PROGNOSE —
                    // kursiv und erklärt, damit sie nie als Ist gelesen wird.
                    const istWert = ic.q_ist?.[qIst];
                    // D-085: Zeile führt q_prognose → Ist nur im ABGESCHLOSSENEN Quartal,
                    // sonst die Raten-Prognose (kursiv). Im laufenden Quartal bleibt das
                    // Ist der vorliegenden Monate als Zusatzzeile sichtbar.
                    // Prompt 130 §1: ein ABGESCHLOSSENES Quartal ohne Ist bleibt leer —
                    // keine Prognose, kein Q-Soll (für neue wie alte Zeilen). Eine
                    // Hochrechnung für ein vergangenes Quartal wäre eine Erfindung.
                    if (status[key] === 'closed' && istWert == null) {
                      return (
                        <td key={key} className="py-1.5 text-right tabular-nums">
                          <span title={T.keinStichtagTitle}>{DASH}</span>
                        </td>
                      );
                    }
                    const qProg = ic.q_prognose?.[qIst];
                    if (qProg != null && !(status[key] === 'closed' && istWert != null)) {
                      return (
                        <td key={key} className="py-1.5 text-right tabular-nums">
                          <span className="italic text-text-muted" title={T.prognoseRateTitle}>
                            {formatEur(qProg)}
                          </span>
                          {istWert != null && (
                            <span className="block text-[10px] text-text-muted">
                              {T.istBisher} {formatEur(istWert)}
                              {markiert.includes(key) && (
                                <span title={T.unvollstaendigHinweis}>*</span>
                              )}
                            </span>
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={key} className="py-1.5 text-right tabular-nums">
                        {istWert != null ? (
                          formatEur(istWert)
                        ) : ic.soll_q != null ? (
                          <span className="italic text-text-muted" title={T.prognoseTitle}>
                            {formatEur(ic.soll_q)}
                          </span>
                        ) : (
                          DASH
                        )}
                      </td>
                    );
                  })
                ) : (
                  <td className="py-1.5 text-right tabular-nums">{formatEur(ic.q1)}</td>
                )}
                {hatQIst && hatJahr && (
                  <td className="py-1.5 text-right tabular-nums">
                    {formatEur(ic.jahr?.prognose)}
                  </td>
                )}
                <td className="py-1.5 text-right tabular-nums">{formatEur(ic.vj)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hatQIst && (
        <div className="mt-2 space-y-1 text-xs text-text-muted">
          {markiert.length > 0 && <p>{T.unvollstaendigHinweis}</p>}
          {hatRate && <p>{T.prognoseRateHinweis}</p>}
          {hatAlt && hatJahr && <p>{T.prognoseHinweis}</p>}
          <p>{T.leerHinweis}</p>
          {zeigtFehlend && <p>{T.fehltTitle}</p>}
        </div>
      )}
    </section>
  );
}
